// Administration → Connecteurs → « Application Shopify »: the owner's Shopify app behind « Se
// connecter avec Shopify » (GET / PUT / DELETE /api/connectors/library/apps/shopify,
// administrators only): its Client ID, its secret (sent once, never shown again: the server gives
// its last 4 characters; an empty field keeps it), the access asked of each store (scopes), the
// Admin API version and, optionally, the app's address in the Dev Dashboard or its install link
// (where a « Se connecter avec Shopify » without a store goes: the store is picked there). The
// steps say what to set in the Dev Dashboard, with the exact App URL (the office's launch address)
// and redirect address to paste (copy buttons). A <details> block shown while the office's
// connector library is on. The app is optional when the server has the Shopify CLI (`cli`: « Se
// connecter avec Shopify » goes through it): a neutral « FACULTATIVE » badge, closed; without the
// CLI it is the only way but a pasted token: « À RÉGLER », open while not set.
import { h, button, input, field, badge, icon } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { errorText } from './model.js';
import { alertBox, copyButton, formError, setBusy } from './common.js';
import './strings.js';

const BASE = '/api/connectors/library/apps/shopify';
const NO_AUTOFILL = { autocomplete: 'off', autocapitalize: 'none', spellcheck: false, 'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '' };
/** Same limit as the server for an install link. */
const LINK_MAX = 2000;

/**
 * Normalises the app as the server sends it.
 * @param {unknown} res
 * @returns {{configured: boolean, clientId: string, secretHint: string, scopes: string, apiVersion: string, redirectUrl: string,
 *   launchUrl: string, organizationId: string, installUrl: string, cli: boolean}} cli: the server has the Shopify CLI
 */
export function shopifyAppOf(res) {
  const r = res && typeof res === 'object' && !Array.isArray(res) ? res : {};
  // eslint-disable-next-line no-control-regex
  const s = (v, max = 500) => (typeof v === 'string' ? v.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, max) : '');
  const organizationId = s(r.organizationId, 20);
  return {
    configured: r.configured === true,
    clientId: s(r.clientId, 128),
    secretHint: s(r.secretHint, 20),
    scopes: s(r.scopes, 2000),
    apiVersion: s(r.apiVersion, 20),
    redirectUrl: s(r.redirectUrl, 1000),
    launchUrl: s(r.launchUrl, 1000),
    organizationId: /^\d+$/.test(organizationId) ? organizationId : '',
    installUrl: s(r.installUrl, LINK_MAX),
    cli: r.cli === true,
  };
}

/**
 * The block's badge: PRÊTE (set), FACULTATIVE (not set, the Shopify CLI on the server does without
 * it), À RÉGLER (not set, no CLI: a pasted token is the only other way).
 * @param {{configured?: boolean, cli?: boolean}|null} app shopifyAppOf()
 * @returns {{key: string, tone: string}}
 */
export function shopifyAppBadge(app) {
  if (app?.configured) return { key: 'adm.shopify.ready', tone: 'success' };
  return app?.cli ? { key: 'adm.shopify.optional', tone: 'neutral' } : { key: 'adm.shopify.notReady', tone: 'amber' };
}

/**
 * What the « Adresse de l’application dans le Dev Dashboard, ou lien d’installation » field shows
 * for the app: its install link, else its address in the Dev Dashboard (its organization), else ''.
 * @param {{organizationId?: string, installUrl?: string}|null} app shopifyAppOf()
 */
export function installLinkOf(app) {
  if (app?.installUrl) return app.installUrl;
  return app?.organizationId ? `https://dev.shopify.com/dashboard/${app.organizationId}` : '';
}

/**
 * Creates the block.
 * @param {object} ctx app context
 * @param {{onChange?: (app: object) => void}} [opts] onChange: the app as read or saved (shopifyAppOf():
 *   the connectors' cards say what « Se connecter » does)
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createShopifyAppBlock(ctx, { onChange = () => {} } = {}) {
  let app = null;
  let destroyed = false;
  let busy = false;
  const idInput = input({ name: 'shopify-client-id', maxLength: 128, ...NO_AUTOFILL });
  const secretInput = input({ name: 'shopify-client-secret', type: 'password', maxLength: 256, ...NO_AUTOFILL });
  const scopesInput = input({ name: 'shopify-scopes', maxLength: 2000, ...NO_AUTOFILL });
  const versionInput = input({ name: 'shopify-api-version', maxLength: 7, placeholder: '2026-10', ...NO_AUTOFILL });
  const linkInput = input({ name: 'shopify-install-link', maxLength: LINK_MAX, inputmode: 'url', placeholder: 'https://dev.shopify.com/dashboard/…', ...NO_AUTOFILL });
  const fields = {
    clientId: field(t('adm.shopify.clientId'), idInput),
    clientSecret: field(t('adm.shopify.clientSecret'), secretInput, { hint: t('adm.shopify.secretHint') }),
    scopes: field(t('adm.shopify.scopes'), scopesInput, { hint: t('adm.shopify.scopesHint') }),
    apiVersion: field(t('adm.shopify.apiVersion'), versionInput, { optional: true }),
    installLink: field(t('adm.shopify.installLink'), linkInput, { hint: t('adm.shopify.installLinkHint'), optional: true }),
  };
  /** An address to paste in the Dev Dashboard, with its copy button. */
  const pasteBox = (get) => {
    const code = h('code', { class: 'adm-cmd-code' });
    return { code, el: h('div', { class: 'adm-cmd' }, code, copyButton(() => get() || '', { toast: ctx.toast, variant: 'ghost' })) };
  };
  const launch = pasteBox(() => app?.launchUrl);
  const redirect = pasteBox(() => app?.redirectUrl);
  const scopes = pasteBox(() => app?.scopes);
  const startNote = h('p', { class: 'muted adm-shopify-start' });
  const errorBox = formError();
  const save = button(t('adm.shopify.save'), { variant: 'primary', size: 'sm', icon: 'check', type: 'submit' });
  const clear = button(t('adm.shopify.clear'), { variant: 'ghost', size: 'sm', icon: 'close', onClick: () => remove() });
  const state = h('span', { class: 'adm-shopify-state' });
  const body = h('div', { class: 'stack adm-shopify-body' }, h('p', { class: 'muted' }, t('adm.loading')));
  const el = h('details', { class: 'adm-shopify' },
    h('summary', { class: 'adm-shopify-summary' }, icon('key', { size: 16 }), h('span', { class: 'adm-shopify-title' }, t('adm.shopify.title')), state),
    body);

  /** Where « Se connecter avec Shopify » without a store goes, as saved. */
  function startText() {
    if (app?.installUrl) return t('adm.shopify.start.install');
    if (app?.organizationId) return t('adm.shopify.start.organization', { id: app.organizationId });
    return t('adm.shopify.start.default');
  }

  function changed() {
    try { onChange(app); } catch (err) { console.error(err); }
  }

  function render() {
    const tag = shopifyAppBadge(app);
    state.replaceChildren(badge(t(tag.key), tag.tone));
    launch.code.textContent = app?.launchUrl || '';
    redirect.code.textContent = app?.redirectUrl || '';
    scopes.code.textContent = app?.scopes || '';
    fields.clientSecret.querySelector('.field-hint').textContent = app?.secretHint
      ? t('adm.shopify.secretKeep', { hint: app.secretHint }) : t('adm.shopify.secretHint');
    startNote.textContent = app?.configured ? startText() : '';
    startNote.hidden = !app?.configured;
    clear.hidden = !app?.configured && !app?.clientId;
    body.replaceChildren(
      // Without the Shopify CLI, never said to be there.
      h('p', { class: 'adm-shopify-intro' }, t(app?.cli ? 'adm.shopify.intro' : 'adm.shopify.introNoCli')),
      h('ol', { class: 'adm-shopify-steps' },
        h('li', {}, h('p', {}, t('adm.shopify.step1'))),
        h('li', {}, h('p', {}, t('adm.shopify.step2')), launch.el),
        h('li', {}, h('p', {}, t('adm.shopify.step3')), redirect.el),
        h('li', {}, h('p', {}, t('adm.shopify.step4')), scopes.el),
        h('li', {}, h('p', {}, t('adm.shopify.step5')))),
      h('form', { class: 'stack adm-shopify-form', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); send(); } },
        fields.clientId, fields.clientSecret, fields.scopes, fields.apiVersion, fields.installLink, startNote, errorBox,
        h('div', { class: 'adm-shopify-foot' }, clear, save)),
      h('p', { class: 'muted adm-shopify-note' }, t('adm.shopify.note')));
  }

  function fill() {
    idInput.value = app?.clientId || '';
    secretInput.value = '';
    scopesInput.value = app?.scopes || '';
    versionInput.value = app?.apiVersion || '';
    linkInput.value = installLinkOf(app);
  }

  async function load() {
    try {
      app = shopifyAppOf(await ctx.api.get(BASE));
      if (destroyed) return;
      fill();
      render();
      // Opened by itself only when it is needed: neither set nor replaced by the Shopify CLI.
      if (!app.configured && !app.cli) el.open = true;
      changed();
    } catch (err) {
      if (!destroyed) body.replaceChildren(alertBox('danger', errorText(err)));
    }
  }

  async function send() {
    if (busy) return;
    for (const f of Object.values(fields)) f.setError(null);
    errorBox.set('');
    busy = true;
    setBusy(save, true);
    try {
      app = shopifyAppOf(await ctx.api.put(BASE, {
        clientId: idInput.value.trim(), clientSecret: secretInput.value.trim(), scopes: scopesInput.value.trim(), apiVersion: versionInput.value.trim(),
        installLink: linkInput.value.trim(),
      }));
      if (destroyed) return;
      fill();
      render();
      changed();
      ctx.toast.success(t('adm.shopify.saved'));
    } catch (err) {
      secretInput.value = '';
      if (destroyed) return;
      const f = err?.details?.field;
      if (f && fields[f]) fields[f].setError(errorText(err));
      else errorBox.set(errorText(err));
    } finally {
      busy = false;
      setBusy(save, false);
    }
  }

  async function remove() {
    if (busy) return;
    const ok = await ctx.overlay.confirm({ title: t('adm.shopify.clearTitle'), message: t('adm.shopify.clearText'), confirmLabel: t('adm.shopify.clear'), danger: true });
    if (!ok || destroyed) return;
    busy = true;
    try {
      app = shopifyAppOf(await ctx.api.del(BASE));
      if (destroyed) return;
      fill();
      render();
      changed();
      ctx.toast.success(t('adm.shopify.cleared'));
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      busy = false;
    }
  }

  load();
  return { el, reload: () => load(), destroy() { destroyed = true; secretInput.value = ''; } };
}
